import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { Send, CheckCircle2, XCircle, Loader2, AlertTriangle, Lock } from "lucide-react";
import { checkREAUploadOutcome } from "../../api/rea";

const PENDING_POLL_INTERVAL_MS = 4000;

/**
 * Modal shown when an agent clicks "Publish as Sold on Portals" on a property
 * that's already marked Sold. Mirrors the Vault workflow agents already know:
 * marking sold is a purely internal status change; telling the portals is a
 * separate, deliberate step where the agent picks which channels to notify.
 *
 * `channels` is the full list this property type could ever support, each
 * `{ key, label, sublabel, disabled, disabledReason }`. Disabled entries (no
 * backend integration yet) render greyed-out and unselectable rather than
 * being omitted, so the picker's shape matches Vault's regardless of what's
 * wired up yet.
 *
 * onPublish(selectedKeys, { confirmReaSold }) should return a promise resolving to
 * `{ results: [{ channel|portal, sent, success, reason }] }` (the exact shape
 * both backend endpoints already return) so per-channel outcomes can be shown.
 *
 * realestate.com.au treats sold details as final: once it has a sale it
 * refuses any change to it. So the first time REA is ticked for a sale
 * (`reaSoldLocked` false) the dialog shows `saleSummary` and requires the
 * agent to confirm before submitting; the backend holds the REA push
 * without that confirmation.
 */

const formatPrice = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? `$${n.toLocaleString("en-AU")}` : "Not set";
};
const formatDate = (value) => {
    if (!value) return "Not set";
    const [y, m, d] = String(value).slice(0, 10).split("-");
    return d && m && y ? `${d}/${m}/${y}` : String(value);
};
const PublishSoldToPortalsModal = ({
    isOpen,
    onClose,
    onPublish,
    address = "",
    channels = [],
    // realestate.com.au already holds this sale's sold details.
    reaSoldLocked = false,
    // { soldPrice, soldDate, suppressSoldPrice } shown for review before the
    // first REA push.
    saleSummary = null,
}) => {
    const [confirmReaSold, setConfirmReaSold] = useState(false);
    const [selected, setSelected] = useState(() =>
        new Set(channels.filter((c) => !c.disabled).map((c) => c.key)),
    );
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState("");

    const toggle = (key) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSubmit = async () => {
        if (selected.size === 0) {
            setError("Select at least one channel to publish to.");
            return;
        }
        if (needsReaReview && !confirmReaSold) {
            setError("Tick the box to confirm the sold details are final on realestate.com.au.");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const res = await onPublish(Array.from(selected), {
                confirmReaSold: needsReaReview ? confirmReaSold : false,
            });
            setResults(res?.results || []);
        } catch (err) {
            setError(err?.response?.data?.detail || "Failed to publish. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setResults(null);
        setError("");
        setConfirmReaSold(false);
        onClose();
    };

    // First REA push for this sale: review the details before they go.
    const reaSelected = selected.has("rea");
    const needsReaReview = reaSelected && !reaSoldLocked && !results;

    const outcomeFor = (key) =>
        results?.find((r) => (r.channel || r.portal) === key);

    // A "pending" result only ever reflects a one-time snapshot from the
    // original request — REA processes uploads asynchronously, so without
    // this the modal would show "Processing" forever even once REA finishes
    // seconds later (QA report, 2026-08-05). Poll each pending channel's
    // upload_id until it resolves to a real outcome.
    useEffect(() => {
        const pendingEntries = (results || []).filter((r) => r.pending && r.upload_id);
        if (pendingEntries.length === 0) return;

        const interval = setInterval(() => {
            pendingEntries.forEach(async (entry) => {
                try {
                    const res = await checkREAUploadOutcome(entry.upload_id);
                    const { outcome, reason } = res.data;
                    if (outcome === "pending") return;
                    setResults((prev) =>
                        (prev || []).map((r) =>
                            r.upload_id === entry.upload_id
                                ? { ...r, pending: false, success: outcome === "success", reason }
                                : r,
                        ),
                    );
                } catch {
                    // Best-effort — leave it pending and try again next tick.
                }
            });
        }, PENDING_POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [results]);

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[440px] rounded-2xl border-none shadow-2xl max-h-[90vh] grid-rows-[auto_1fr_auto] overflow-hidden">
                <DialogHeader>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-cyan-100 text-[#3E9BA4]">
                            <Send className="w-6 h-6" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                                Publish as Sold on Portals
                            </DialogTitle>
                            {address ? (
                                <p className="text-xs font-medium text-gray-400 mt-1 truncate max-w-[280px]">
                                    {address}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-2 space-y-1 overflow-y-auto min-h-0 -mr-2 pr-2">
                    <p className="text-xs font-medium text-gray-400 mb-3">
                        Choose which channels should reflect this sale. Unselected
                        channels are left as-is.
                    </p>

                    {channels.map((c) => {
                        const outcome = outcomeFor(c.key);
                        return (
                            <div
                                key={c.key}
                                className={`flex items-center justify-between py-2.5 px-1 rounded-lg ${
                                    c.disabled ? "opacity-50" : ""
                                }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <input
                                        type="checkbox"
                                        checked={selected.has(c.key)}
                                        disabled={c.disabled || loading || !!results}
                                        onChange={() => toggle(c.key)}
                                        className="w-3.5 h-3.5 rounded border-gray-300 text-[#55C5D0] focus:ring-[#55C5D0] cursor-pointer disabled:cursor-not-allowed"
                                    />
                                    <div>
                                        <span className="text-[13px] text-gray-900 font-semibold block">
                                            {c.label}
                                        </span>
                                        {c.disabled && c.disabledReason ? (
                                            <span className="text-[11px] text-gray-400">
                                                {c.disabledReason}
                                            </span>
                                        ) : c.sublabel ? (
                                            <span className="text-[11px] text-gray-400">
                                                {c.sublabel}
                                            </span>
                                        ) : null}
                                        {outcome && !outcome.success && !outcome.pending && outcome.reason ? (
                                            // Show the portal's own reason (e.g. REA refusing a
                                            // change to a sold listing) rather than a bare "Failed".
                                            <span
                                                className={`mt-0.5 block text-[11px] font-medium ${
                                                    outcome.needs_confirmation ? "text-amber-700" : "text-red-500"
                                                }`}
                                            >
                                                {outcome.reason}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>

                                {outcome ? (
                                    outcome.pending ? (
                                        // REA processes uploads asynchronously — this means it
                                        // hadn't finished within our check window, not that it failed.
                                        <span
                                            className="flex items-center gap-1 text-[11px] font-bold text-amber-600"
                                            title={outcome.reason}
                                        >
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing
                                        </span>
                                    ) : outcome.success ? (
                                        <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Published
                                        </span>
                                    ) : outcome.needs_confirmation ? (
                                        <span className="flex items-center gap-1 text-[11px] font-bold text-amber-600">
                                            <AlertTriangle className="w-3.5 h-3.5" /> Not sent
                                        </span>
                                    ) : (
                                        <span
                                            className="flex items-center gap-1 text-[11px] font-bold text-red-500"
                                            title={outcome.reason}
                                        >
                                            <XCircle className="w-3.5 h-3.5" /> Failed
                                        </span>
                                    )
                                ) : null}
                            </div>
                        );
                    })}

                    {needsReaReview ? (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                            <p className="flex gap-2 text-[12px] font-bold text-amber-800">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                Check these before they go to realestate.com.au
                            </p>
                            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
                                <dt className="text-gray-500">Sold price</dt>
                                <dd className="font-semibold text-gray-900">{formatPrice(saleSummary?.soldPrice)}</dd>
                                <dt className="text-gray-500">Sold date</dt>
                                <dd className="font-semibold text-gray-900">{formatDate(saleSummary?.soldDate)}</dd>
                                <dt className="text-gray-500">Price shown</dt>
                                <dd className="font-semibold text-gray-900">
                                    {saleSummary?.suppressSoldPrice ? "No (Contact Agent)" : "Yes"}
                                </dd>
                            </dl>
                            <label className="flex items-start gap-2 text-[12px] font-medium text-amber-900 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={confirmReaSold}
                                    onChange={(e) => {
                                        setConfirmReaSold(e.target.checked);
                                        if (error) setError("");
                                    }}
                                    disabled={loading}
                                    className="mt-0.5 w-3.5 h-3.5 rounded border-amber-300 text-[#55C5D0] focus:ring-[#55C5D0]"
                                />
                                These details are correct. I understand they are final on realestate.com.au and can&rsquo;t be changed after publishing.
                            </label>
                            <p className="text-[11px] text-amber-700">
                                Wrong? Close this and use Edit Sale Details first.
                            </p>
                        </div>
                    ) : null}

                    {reaSelected && reaSoldLocked && !results ? (
                        <p className="mt-3 flex gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-[12px] font-medium text-gray-600">
                            <Lock className="w-4 h-4 flex-shrink-0" />
                            realestate.com.au already has this sale. It won&rsquo;t accept changes to the sold details; contact REA support to correct them.
                        </p>
                    ) : null}

                    {error ? (
                        <p className="text-xs font-medium text-red-500 mt-2">{error}</p>
                    ) : null}

                    {results ? (
                        <p className="text-[11px] font-medium text-gray-400 mt-3">
                            {results.some((r) => r.pending)
                                ? "Processing channels update automatically — keep this open, or check back shortly. Failed channels can be retried by opening this again."
                                : "Failed channels can be retried by opening this again."}
                        </p>
                    ) : null}
                </div>

                <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
                    <button
                        onClick={handleClose}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
                    >
                        {results ? "Close" : "Cancel"}
                    </button>
                    {!results ? (
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-[#55C5D0] hover:bg-[#4AADB7] focus:ring-[#55C5D0]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Publishing...
                                </>
                            ) : (
                                "Submit"
                            )}
                        </button>
                    ) : null}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PublishSoldToPortalsModal;
